import React, { useState, useEffect } from 'react';
import { 
  Bell, Shield, Globe, Moon, Sun, Check, Loader2, User, Lock, CreditCard, 
  MapPin, Mail, Smartphone, Eye, EyeOff, ChevronRight, Download, Trash2,
  MessageSquare, ShoppingBag, Heart, Gift, Languages, DollarSign, Monitor,
  Key, Smartphone as TwoFactor, LogOut, AlertTriangle, Link as LinkIconLucide
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../api/auth';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface UserSettings {
  notifications: {
    orderUpdates: boolean;
    promotional: boolean;
    newsletter: boolean;
    sms: boolean;
    push: boolean;
  };
  privacy: {
    profileVisible: boolean;
    activityVisible: boolean;
    showOrders: boolean;
  };
  appearance: {
    theme: string;
  };
  language: string;
  currency: string;
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [settings, setSettings] = useState<UserSettings>({
    notifications: {
      orderUpdates: true,
      promotional: false,
      newsletter: true,
      sms: false,
      push: true
    },
    privacy: {
      profileVisible: false,
      activityVisible: false,
      showOrders: true
    },
    appearance: {
      theme: 'light'
    },
    language: 'en',
    currency: 'INR'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState('account');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [connectedAccounts, setConnectedAccounts] = useState({ google: false, facebook: false });
  const [connectingAccount, setConnectingAccount] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchConnectedAccounts();
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.data) {
        setConnectedAccounts({
          google: !!data.data.googleId,
          facebook: !!data.data.facebookId
        });
      }
    } catch (error) {
      console.error('Failed to fetch connected accounts:', error);
    }
  };

  const handleConnectAccount = async (provider: 'google' | 'facebook') => {
    try {
      setConnectingAccount(provider);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/users/link-${provider}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success || data.url) {
        // If OAuth URL returned, redirect
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        setConnectedAccounts(prev => ({ ...prev, [provider]: true }));
        alert(`${provider.charAt(0).toUpperCase() + provider.slice(1)} connected successfully!`);
      } else {
        alert(data.message || `Failed to connect ${provider}`);
      }
    } catch (error) {
      console.error(`Failed to connect ${provider}:`, error);
      alert(`Could not connect ${provider}. Check your network and try again.`);
    } finally {
      setConnectingAccount(null);
    }
  };

  const handleDisconnectAccount = async (provider: 'google' | 'facebook') => {
    if (!window.confirm(`Are you sure you want to disconnect your ${provider} account?`)) return;
    
    try {
      setConnectingAccount(provider);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/unlink-${provider}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setConnectedAccounts(prev => ({ ...prev, [provider]: false }));
        alert(`${provider.charAt(0).toUpperCase() + provider.slice(1)} disconnected successfully!`);
      } else {
        alert(data.message || `Failed to disconnect ${provider}`);
      }
    } catch (error) {
      console.error(`Failed to disconnect ${provider}:`, error);
      alert(`Could not disconnect ${provider}. Please try again.`);
    } finally {
      setConnectingAccount(null);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // First load from localStorage
      const localSettings = localStorage.getItem('userSettings');
      if (localSettings) {
        setSettings(prev => ({ ...prev, ...JSON.parse(localSettings) }));
      }
      
      // Then try to load from API
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.data?.settings) {
        setSettings(prev => ({ ...prev, ...data.data.settings }));
        // Save to localStorage for offline access
        localStorage.setItem('userSettings', JSON.stringify(data.data.settings));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: Partial<UserSettings>) => {
    try {
      setSaving(true);
      // Save to localStorage immediately for instant feedback
      const currentSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
      const mergedSettings = { ...currentSettings, ...newSettings };
      localStorage.setItem('userSettings', JSON.stringify(mergedSettings));
      
      // Update state immediately
      setSettings(prev => ({ ...prev, ...newSettings }));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      // Try to save to API
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSettings)
      });
      const data = await response.json();
      if (data.success) {
        console.log('Settings saved to server');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      // Settings already saved to localStorage as fallback
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = async (key: string, value: boolean) => {
    const newSettings = { ...settings, notifications: { ...settings.notifications, [key]: value } };
    setSettings(newSettings);
    await saveSettings({ notifications: newSettings.notifications });
  };

  const handlePrivacyChange = async (key: string, value: boolean) => {
    const newSettings = { ...settings, privacy: { ...settings.privacy, [key]: value } };
    setSettings(newSettings);
    await saveSettings({ privacy: newSettings.privacy });
  };

  const handleThemeChange = async (theme: string) => {
    const newSettings = { ...settings, appearance: { ...settings.appearance, theme } };
    setSettings(newSettings);
    await saveSettings({ appearance: newSettings.appearance });
  };

  const handlePasswordChange = async () => {
    if (passwordData.new !== passwordData.confirm) {
      alert('New passwords do not match');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword: passwordData.current, newPassword: passwordData.new })
      });
      const data = await response.json();
      if (data.success) {
        setShowPasswordModal(false);
        setPasswordData({ current: '', new: '', confirm: '' });
        alert('Password changed successfully!');
      } else {
        alert(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      alert('Failed to change password');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        void logout().then(() => navigate('/login'));
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const handleLogout = () => {
    void logout().then(() => navigate('/login'));
  };

  const sections = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy & Security', icon: Shield },
    { id: 'preferences', label: 'Preferences', icon: Globe },
    { id: 'connected', label: 'Linked Accounts', icon: LinkIconLucide },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-20 lg:pb-8">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Settings</h1>
              <p className="text-sm text-gray-500">Manage your account preferences</p>
            </div>
            {saveSuccess && (
              <div className="flex items-center text-green-600 bg-green-50 px-4 py-2 rounded-xl">
                <Check className="w-5 h-5 mr-2" />
                <span className="font-medium">Saved</span>
              </div>
            )}
            {saving && (
              <div className="flex items-center text-blue-600 bg-blue-50 px-4 py-2 rounded-xl">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                <span className="font-medium">Saving...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Mobile Tab Navigation */}
        <div className="lg:hidden mb-4 overflow-x-auto flex gap-2 pb-2 -mx-4 px-4">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap transition-all ${
                  activeSection === section.id
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-white text-gray-600 shadow-sm'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{section.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm p-2 sticky top-24">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeSection === section.id
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
              <hr className="my-2 border-gray-200" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            {/* Account Section */}
            {activeSection === 'account' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Account Information
                  </h2>
                  <div className="space-y-4">
                    <div 
                      onClick={() => navigate('/profile')}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Profile Details</p>
                          <p className="text-sm text-gray-500">Name, email, phone</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <div 
                      onClick={() => setShowPasswordModal(true)}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Lock className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Change Password</p>
                          <p className="text-sm text-gray-500">Update your password</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <div 
                      onClick={() => navigate('/addresses')}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Saved Addresses</p>
                          <p className="text-sm text-gray-500">Manage delivery addresses</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <div 
                      onClick={() => navigate('/payment-methods')}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Payment Methods</p>
                          <p className="text-sm text-gray-500">Manage saved cards</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Danger Zone
                  </h2>
                  <div 
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center justify-between p-4 bg-red-50 rounded-xl cursor-pointer hover:bg-red-100 transition-colors border border-red-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                        <Trash2 className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-red-700">Delete Account</p>
                        <p className="text-sm text-red-500">Permanently delete your account</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-400" />
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  Notification Preferences
                </h2>
                <div className="space-y-4">
                  {[
                    { key: 'orderUpdates', label: 'Order Updates', desc: 'Get notified about order status changes', icon: ShoppingBag },
                    { key: 'promotional', label: 'Promotional', desc: 'Receive deals and special offers', icon: Gift },
                    { key: 'newsletter', label: 'Newsletter', desc: 'Weekly updates and new product alerts', icon: Mail },
                    { key: 'sms', label: 'SMS Notifications', desc: 'Receive updates via SMS', icon: Smartphone },
                    { key: 'push', label: 'Push Notifications', desc: 'Browser push notifications', icon: Bell },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                            <Icon className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{item.label}</p>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.notifications[item.key as keyof typeof settings.notifications] as boolean}
                            onChange={(e) => handleNotificationChange(item.key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r from-blue-600 to-indigo-600"></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Privacy Section */}
            {activeSection === 'privacy' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Privacy Settings
                  </h2>
                  <div className="space-y-4">
                    {[
                      { key: 'profileVisible', label: 'Profile Visibility', desc: 'Allow others to see your profile' },
                      { key: 'activityVisible', label: 'Activity Status', desc: 'Show when you are online' },
                      { key: 'showOrders', label: 'Order History', desc: 'Allow others to view your order history' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div>
                          <p className="font-medium text-gray-900">{item.label}</p>
                          <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.privacy[item.key as keyof typeof settings.privacy] as boolean}
                            onChange={(e) => handlePrivacyChange(item.key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r from-blue-600 to-indigo-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Download className="w-5 h-5 text-blue-600" />
                    Your Data
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Download className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Download Your Data</p>
                          <p className="text-sm text-gray-500">Get a copy of all your data</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Section */}
            {activeSection === 'preferences' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  App Preferences
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Languages className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Language</p>
                        <p className="text-sm text-gray-500">Select your preferred language</p>
                      </div>
                    </div>
                    <select
                      value={settings.language}
                      onChange={(e) => saveSettings({ language: e.target.value })}
                      className="px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                    >
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Currency</p>
                        <p className="text-sm text-gray-500">Select your preferred currency</p>
                      </div>
                    </div>
                    <select
                      value={settings.currency}
                      onChange={(e) => saveSettings({ currency: e.target.value })}
                      className="px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                    >
                      <option value="INR">INR (India)</option>
                      <option value="USD">USD (US Dollar)</option>
                      <option value="EUR">EUR (Euro)</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        {settings.appearance.theme === 'dark' ? <Moon className="w-5 h-5 text-amber-600" /> : <Sun className="w-5 h-5 text-amber-600" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Theme</p>
                        <p className="text-sm text-gray-500">Choose light or dark mode</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleThemeChange('light')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                          settings.appearance.theme === 'light'
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Sun className="w-4 h-4" />
                        Light
                      </button>
                      <button
                        onClick={() => handleThemeChange('dark')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                          settings.appearance.theme === 'dark'
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Moon className="w-4 h-4" />
                        Dark
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Linked Accounts Section */}
            {activeSection === 'connected' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <LinkIconLucide className="w-5 h-5 text-blue-600" />
                  Connected Accounts
                </h2>
                <div className="space-y-4">
                  {/* Google */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#4285F4"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Google</p>
                        <p className={`text-sm ${connectedAccounts.google ? 'text-green-600' : 'text-gray-500'}`}>
                          {connectedAccounts.google ? 'Connected' : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    {connectingAccount === 'google' ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    ) : connectedAccounts.google ? (
                      <button 
                        onClick={() => handleDisconnectAccount('google')}
                        className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Disconnect
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleConnectAccount('google')}
                        className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                  {/* Facebook */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Facebook</p>
                        <p className={`text-sm ${connectedAccounts.facebook ? 'text-green-600' : 'text-gray-500'}`}>
                          {connectedAccounts.facebook ? 'Connected' : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    {connectingAccount === 'facebook' ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    ) : connectedAccounts.facebook ? (
                      <button 
                        onClick={() => handleDisconnectAccount('facebook')}
                        className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Disconnect
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleConnectAccount('facebook')}
                        className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Change Password</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current}
                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordChange}
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Account</h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently lost.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;